// Status bar (.status-bar). Live snapshot L1454-1468: object count, label size,
// a Shortcuts link (keyboard icon, opens the shortcuts modal), a privacy note,
// a flex spacer, the design name, the auto-save indicator, and the brand.
//
// Keeps the #statusObjects span (tests + size picker refresh it) and adds the
// rest of the live chrome. The Shortcuts link opens a modal via ui/dom openModal.
import { state } from './state'
import { icon } from './ui/icons'
import { el, openModal } from './ui/dom'
import { PRODUCT } from './product'

/** One-time scaffold of the status-bar children into #statusBar. */
function ensureScaffold(bar: HTMLElement): void {
  if (bar.querySelector('#statusSize')) return
  bar.replaceChildren()

  const objects = bar.querySelector<HTMLElement>('#statusObjects') ?? el('span')
  objects.id = 'statusObjects'
  objects.textContent = '0 objects'

  const size = el('span')
  size.id = 'statusSize'
  size.textContent = ''

  // Shortcuts link: keyboard icon + label, opens the shortcuts modal.
  const shortcuts = el('span', 'cursor-pointer hover:text-slate-300 transition-colors inline-flex items-center gap-1')
  shortcuts.title = 'Keyboard shortcuts (?)'
  shortcuts.innerHTML = icon('keyboard', 'w-3 h-3')
  shortcuts.append(document.createTextNode('Shortcuts'))
  shortcuts.addEventListener('click', openShortcutsModal)

  const privacy = el('span', '', 'Your data stays private — the editor runs in your browser')

  const spacer = el('div', 'flex-1')

  const designName = el('span', 'text-[11px] text-slate-400 cursor-pointer hover:text-slate-200 truncate')
  designName.id = 'designNameStatus'
  designName.style.maxWidth = '180px'
  designName.title = 'Current design'

  const sep = el('span', 'text-slate-600 mx-1', '·')
  sep.id = 'designNameSep'
  sep.style.display = 'none'

  const autoSave = el('span', 'text-[11px] text-slate-500 transition-all duration-300')
  autoSave.id = 'autoSaveIndicator'

  const brandSep = el('span', 'text-slate-600 mx-1', '·')
  const brand = el('span', 'text-slate-500', PRODUCT.brandDomainLabel)

  bar.append(objects, sep0(), size, sep0(), shortcuts, sep0(), privacy, spacer, designName, sep, autoSave, brandSep, brand)
}

/** A thin "|" divider matching the live status bar. */
function sep0(): HTMLElement {
  return el('span', '', '|')
}

export function updateStatus(): void {
  const bar = document.getElementById('statusBar')
  if (!bar) {
    // Fallback: at least keep #statusObjects fresh if the footer is bare.
    const lone = document.getElementById('statusObjects')
    if (lone) lone.textContent = objectsText()
    return
  }
  ensureScaffold(bar)

  const objects = document.getElementById('statusObjects')
  if (objects) objects.textContent = objectsText()

  const size = document.getElementById('statusSize')
  if (size) size.textContent = sizeText()

  const designName = document.getElementById('designNameStatus')
  const sep = document.getElementById('designNameSep')
  const name = state.currentDesignName?.trim()
  if (designName && sep) {
    if (name && name !== 'Untitled') {
      designName.textContent = name
      designName.style.display = ''
      sep.style.display = ''
    } else {
      designName.textContent = ''
      designName.style.display = 'none'
      sep.style.display = 'none'
    }
  }
}

function objectsText(): string {
  const c = state.canvas
  const count = c ? c.getObjects().filter((o) => !(o as any).isGrid && !(o as any).isBgImage).length : 0
  return `${count} object${count === 1 ? '' : 's'}`
}

function sizeText(): string {
  if (state.currentSheet) return `sheet: ${state.currentSheet.name}`
  const s = state.currentSize
  const w = state.orientation === 'portrait' ? s.heightMM : s.widthMM
  const h = state.orientation === 'portrait' ? s.widthMM : s.heightMM
  return `${w} × ${h} mm`
}

/** Keyboard-shortcuts reference dialog (live L1790-1859), built via openModal. */
export function openShortcutsModal(): void {
  const sections: Array<[string, Array<[string, string]>]> = [
    ['General', [
      ['Ctrl Z', 'Undo'], ['Ctrl Y', 'Redo'], ['Ctrl D', 'Duplicate'],
      ['Ctrl A', 'Select all'], ['Ctrl C', 'Copy'], ['Ctrl V', 'Paste'], ['Del', 'Delete'],
    ]],
    ['Group', [
      ['Ctrl G', 'Group objects'], ['Ctrl Shift G', 'Ungroup'],
    ]],
    ['Tools', [
      ['V', 'Select'], ['T', 'Add text'], ['R', 'Add rectangle'],
    ]],
    ['File', [
      ['Ctrl S', 'Save (auto)'], ['Ctrl E', 'Export'], ['Ctrl P', 'Print'],
    ]],
    ['View', [
      ['0', 'Fit to view'], ['Ctrl +', 'Zoom in'], ['Ctrl −', 'Zoom out'],
    ]],
  ]

  const grid = el('div', 'grid grid-cols-2 gap-6')
  const left = el('div', 'space-y-5')
  const right = el('div', 'space-y-5')
  sections.forEach(([heading, rows], i) => {
    const block = el('div')
    const t = el('div', 'sc-section-title', heading)
    const table = el('table', 'sc-table')
    for (const [keys, label] of rows) {
      const tr = el('tr')
      const tdK = el('td')
      keys.split(' ').forEach((k, j) => {
        if (j > 0) tdK.append(document.createTextNode(' '))
        tdK.append(el('kbd', 'sc-key', k))
      })
      const tdL = el('td', '', label)
      tr.append(tdK, tdL)
      table.append(tr)
    }
    block.append(t, table)
    ;(i < 2 ? left : right).append(block)
  })
  grid.append(left, right)

  openModal({ title: 'Keyboard Shortcuts', body: grid, width: '580px', testid: 'shortcutsModal' })
}
